import React, { useState, useEffect, useRef } from 'react';
import { 
  Collapse, 
  Radio, 
  Button, 
  Typography, 
  Space, 
  Input, 
  Spin,
  Tooltip,
  Checkbox,
  message 
} from 'antd';
import { 
  DownOutlined, 
  RightOutlined, 
  InfoCircleOutlined,
  UndoOutlined,
  LoadingOutlined,
  CheckOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;

/**
 * ReviewPanel - A generic component for reviewing categorized items with feedback capability
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing review panel configuration and items
 * @param {string} props.title - The title of the accordion (fallback if not in data)
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 * @param {string} props.sessionId - The current session ID
 * @param {Object} props.activeAgent - Active agent configuration with endpoint
 * @param {Function} props.onComponentUpdate - Callback to update other components
 * @param {string} props.project_id - Project ID
 * @param {string} props.agent_key - Agent key for API calls
 */
const ReviewPanel = ({
  data,
  title = "Review Panel",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  sessionId,
  activeAgent,
  onComponentUpdate,
  project_id,
  agent_key
}) => {
  // State for accordion expansion
  const [isExpanded, setIsExpanded] = useState(true);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState(null);
  
  // State for expanded feedback items (keyed by item_id)
  const [expandedFeedback, setExpandedFeedback] = useState({});
  
  // State for feedback text input (keyed by item_id)
  const [feedbackText, setFeedbackText] = useState({});
  
  // State for mark for review changes (keyed by item_id, tracks local changes before save)
  const [markedForReview, setMarkedForReview] = useState({});
  
  // State for loading states (keyed by item_id for info button loading)
  const [loadingInfo, setLoadingInfo] = useState({});
  
  // State for feedback submission loading
  const [submittingFeedback, setSubmittingFeedback] = useState({});
  
  // State for affirm action loading
  const [affirmingItem, setAffirmingItem] = useState({});
  
  // State to force re-render when needed
  const [updateCounter, setUpdateCounter] = useState(0);
  
  // Ref for expand icon handling
  const expandIconRef = useRef(null);

  // Initialize active tab when data changes
  useEffect(() => {
    if (data?.view_mode?.type === 'tabbed' && data?.view_mode?.tabs?.length > 0) {
      const defaultTab = data.view_mode.default_tab || data.view_mode.tabs[0].id;
      setActiveTab(defaultTab);
    } else {
      setActiveTab('default');
    }
  }, [data]);

  // Register component for updates
  useEffect(() => {
    if (registerComponent && uuid) {
      const refreshComponent = () => {
        console.log(`Refreshing ReviewPanel component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      const unregister = registerComponent(refreshComponent);
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Handle expand state changes
  useEffect(() => {
    if (expandIconRef.current !== null) {
      setIsExpanded(expandIconRef.current);
      expandIconRef.current = null;
    }
  }, []);

  // Handler for tab change
  const handleTabChange = (e) => {
    setActiveTab(e.target.value);
  };

  // Handler for info button click
  const handleInfoClick = async (item, sectionId) => {
    if (!activeAgent?.endpoint) {
      message.error('No endpoint configured');
      return;
    }

    setLoadingInfo(prev => ({ ...prev, [item.item_id]: true }));

    try {
      // Find the action configuration for view_details
      const detailAction = data?.item_actions?.find(a => a.action_id === 'view_details');
      const endpoint = detailAction?.endpoint || '/generate_reasoning';

      const response = await fetch(`${activeAgent.endpoint}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          item_id: item.item_id,
          detail_request_payload: item.detail_request_payload,
          section_id: sectionId,
          tab_id: activeTab,
          project_id: project_id,
          agent_key: agent_key
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const result = await response.json();

      // Handle component updates (backend controls which components to refresh)
      if (result.updatedComponentIds && onComponentUpdate) {
        onComponentUpdate(result);
      }

    } catch (error) {
      console.error('Error fetching details:', error);
      message.error(`Failed to fetch details: ${error.message}`);
    } finally {
      setLoadingInfo(prev => ({ ...prev, [item.item_id]: false }));
    }
  };

  // Handler for feedback toggle
  const handleFeedbackToggle = (itemId) => {
    setExpandedFeedback(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Handler for feedback text change
  const handleFeedbackChange = (itemId, value) => {
    setFeedbackText(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  // Handler for feedback submission
  const handleFeedbackSubmit = async (item, sectionId) => {
    const feedback = feedbackText[item.item_id];
    
    if (!feedback?.trim()) {
      message.warning('Please enter feedback before submitting');
      return;
    }

    if (!activeAgent?.endpoint) {
      message.error('No endpoint configured');
      return;
    }

    setSubmittingFeedback(prev => ({ ...prev, [item.item_id]: true }));

    try {
      const feedbackAction = data?.item_actions?.find(a => a.action_id === 'feedback');
      const endpoint = feedbackAction?.endpoint || '/save_correction';

      const response = await fetch(`${activeAgent.endpoint}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          item_id: item.item_id,
          feedback: feedback,
          section_id: sectionId,
          tab_id: activeTab,
          project_id: project_id,
          agent_key: agent_key
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const result = await response.json();

      // Collapse the feedback input
      setExpandedFeedback(prev => ({
        ...prev,
        [item.item_id]: false
      }));

      // Clear the input
      setFeedbackText(prev => ({
        ...prev,
        [item.item_id]: ''
      }));

      message.success('Feedback submitted successfully');

      // Handle component updates - backend will return updated item with feedback
      if (onComponentUpdate) {
        onComponentUpdate(result);
      }

    } catch (error) {
      console.error('Error submitting feedback:', error);
      message.error(`Failed to submit feedback: ${error.message}`);
    } finally {
      setSubmittingFeedback(prev => ({ ...prev, [item.item_id]: false }));
    }
  };

  // Handler for mark for review checkbox
  const handleMarkForReview = async (item, sectionId, checked) => {
    // Update local state immediately for responsive UI
    setMarkedForReview(prev => ({
      ...prev,
      [item.item_id]: checked
    }));

    // If auto-save is enabled, save to backend
    if (data?.review_config?.auto_save_marks && activeAgent?.endpoint) {
      try {
        const endpoint = data?.review_config?.mark_review_endpoint || '/mark_for_review';

        const response = await fetch(`${activeAgent.endpoint}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionId}`
          },
          body: JSON.stringify({
            item_id: item.item_id,
            marked_for_review: checked,
            section_id: sectionId,
            tab_id: activeTab,
            project_id: project_id,
            agent_key: agent_key
          }),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const result = await response.json();

        // Handle component updates if any
        if (result.updatedComponentIds && onComponentUpdate) {
          onComponentUpdate(result);
        }

      } catch (error) {
        console.error('Error saving mark for review:', error);
        // Revert local state on error
        setMarkedForReview(prev => ({
          ...prev,
          [item.item_id]: !checked
        }));
        message.error('Failed to save review mark');
      }
    }
  };

  // Handler for section-specific actions (like restore)
  const handleSectionAction = async (action, item, sectionId) => {
    if (!activeAgent?.endpoint) {
      message.error('No endpoint configured');
      return;
    }

    try {
      const response = await fetch(`${activeAgent.endpoint}${action.endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          item_id: item.item_id,
          action_id: action.action_id,
          source_section: sectionId,
          target_section: action.target_section,
          tab_id: activeTab,
          project_id: project_id,
          agent_key: agent_key
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const result = await response.json();

      message.success(result.message || 'Action completed successfully');

      // Handle component updates
      if (result.updatedComponentIds && onComponentUpdate) {
        onComponentUpdate(result);
      }

    } catch (error) {
      console.error('Error performing action:', error);
      message.error(`Failed to perform action: ${error.message}`);
    }
  };

  // Handler for affirming codes from negated/historical/uncertain sections
  const handleAffirm = async (item, sectionId) => {
    if (!activeAgent?.endpoint) {
      message.error('No endpoint configured');
      return;
    }

    setAffirmingItem(prev => ({ ...prev, [item.item_id]: true }));

    try {
      const response = await fetch(`${activeAgent.endpoint}/affirm_code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          item_id: item.item_id,
          source_section: sectionId,
          tab_id: activeTab,
          project_id: project_id,
          agent_key: agent_key
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const result = await response.json();

      message.success(result.message || 'Code affirmed successfully');

      // Handle component updates
      if (result.updatedComponentIds && onComponentUpdate) {
        onComponentUpdate(result);
      }

    } catch (error) {
      console.error('Error affirming code:', error);
      message.error(`Failed to affirm code: ${error.message}`);
    } finally {
      setAffirmingItem(prev => ({ ...prev, [item.item_id]: false }));
    }
  };

  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };

  // Get sections for the active tab
  const getActiveSections = () => {
    if (!data?.content) return [];
    
    if (data.view_mode?.type === 'tabbed') {
      return data.content[activeTab] || [];
    }
    
    return data.content['default'] || data.content[Object.keys(data.content)[0]] || [];
  };

  // Get section-specific item actions
  const getSectionItemActions = (sectionId) => {
    return data?.section_item_actions?.[sectionId] || [];
  };

  // Get feedback action config
  const getFeedbackAction = () => {
    return data?.item_actions?.find(a => a.action_id === 'feedback');
  };

  // Check if mark for review is enabled
  const isMarkForReviewEnabled = () => {
    return data?.review_config?.enable_mark_for_review !== false;
  };

  // Get marked for review state (local state takes precedence, then item data)
  const getMarkedForReview = (itemId, itemMarkedForReview) => {
    if (markedForReview.hasOwnProperty(itemId)) {
      return markedForReview[itemId];
    }
    return itemMarkedForReview || false;
  };

  // Render a single item row
  const renderItem = (item, sectionId) => {
    const isInfoLoading = loadingInfo[item.item_id];
    const isFeedbackExpanded = expandedFeedback[item.item_id];
    const isFeedbackSubmitting = submittingFeedback[item.item_id];
    const existingFeedback = item.feedback; // Read from item data
    const sectionActions = getSectionItemActions(sectionId);
    const feedbackAction = getFeedbackAction();
    const feedbackLabel = feedbackAction?.label || 'Feedback';
    const isMarked = getMarkedForReview(item.item_id, item.marked_for_review);
    
    // Suppress Reject button for llm_flagged, user_rejected, user_affirmed, and negated sections
    const showRejectButton = sectionId !== 'llm_flagged' && sectionId !== 'user_rejected' && sectionId !== 'user_affirmed' && sectionId !== 'negated';
    
    // Show Affirm button for negated, historical, and uncertain sections
    const showAffirmButton = ['negated', 'historical', 'uncertain'].includes(sectionId);
    const isAffirming = affirmingItem[item.item_id];

    return (
      <div key={item.item_id} style={{ marginBottom: '8px' }}>
        {/* Main item row */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px'
        }}>
          {/* Mark for review checkbox */}
          {isMarkForReviewEnabled() && (
            <Tooltip title="Mark for later review">
              <Checkbox
                checked={isMarked}
                onChange={(e) => handleMarkForReview(item, sectionId, e.target.checked)}
              />
            </Tooltip>
          )}

          {/* Text display box */}
          <div style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: '4px',
            backgroundColor: isMarked ? '#fffbe6' : 'transparent',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'nowrap'
          }}>
            <Text strong style={{ whiteSpace: 'nowrap', fontSize: '14px' }}>{item.primary_text}</Text>
            {item.secondary_text && (
              <Text style={{ fontSize: '14px', color: '#000' }}> - {item.secondary_text}</Text>
            )}
            {item.confidence !== undefined && sectionId !== 'llm_flagged' && (
              <Text type="secondary" style={{ marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: '14px' }}>
                ({Math.round(item.confidence * 100)}%)
              </Text>
            )}
            {/* Badges */}
            {item.badges?.map((badge, idx) => (
              <Tooltip key={idx} title={badge.tooltip}>
                <span style={{
                  backgroundColor: '#f3e8ff',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#6200EE',
                  whiteSpace: 'nowrap',
                  border: '1px solid #d4b5ff'
                }}>
                  {badge.type === 'inferred' ? '⚡' : ''} {badge.label || badge.type}
                </span>
              </Tooltip>
            ))}
          </div>

          {/* Info button */}
          <Tooltip title="View reasoning">
            <Button
              type="text"
              shape="circle"
              icon={isInfoLoading ? 
                <LoadingOutlined style={{ fontSize: '16px' }} /> : 
                <InfoCircleOutlined style={{ fontSize: '18px' }} />
              }
              onClick={() => handleInfoClick(item, sectionId)}
              disabled={isInfoLoading}
              style={{
                border: '1px solid #d9d9d9',
                width: '36px',
                height: '36px'
              }}
            />
          </Tooltip>

          {/* Feedback/Reject button - hidden for llm_flagged section */}
          {showRejectButton && (
            <Button
              type="primary"
              onClick={() => handleFeedbackToggle(item.item_id)}
              style={{
                backgroundColor: '#6200EE',
                borderColor: '#6200EE',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minWidth: '100px'
              }}
            >
              {feedbackLabel}
              {isFeedbackExpanded ? 
                <span style={{ fontSize: '10px' }}>▲</span> : 
                <span style={{ fontSize: '10px' }}>▼</span>
              }
            </Button>
          )}

          {/* Affirm button - shown for negated, historical, uncertain sections */}
          {showAffirmButton && (
            <Tooltip title="Affirm this code">
              <Button
                type="default"
                onClick={() => handleAffirm(item, sectionId)}
                loading={isAffirming}
                icon={!isAffirming && <CheckOutlined />}
                style={{
                  borderColor: '#389e0d',
                  color: '#389e0d',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: 500
                }}
              >
                Affirm
              </Button>
            </Tooltip>
          )}

          {/* Section-specific actions (e.g., Restore) */}
          {sectionActions.map((action) => (
            <Tooltip key={action.action_id} title={action.tooltip}>
              <Button
                type="text"
                icon={action.icon === 'undo' ? <UndoOutlined /> : null}
                onClick={() => handleSectionAction(action, item, sectionId)}
                style={{
                  border: '1px solid #d9d9d9'
                }}
              >
                {action.label}
              </Button>
            </Tooltip>
          ))}
        </div>

        {/* Feedback input area (expandable) - hidden for llm_flagged section */}
        {showRejectButton && isFeedbackExpanded && (
          <div style={{
            marginTop: '8px',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            backgroundColor: '#fafafa'
          }}>
            <TextArea
              rows={3}
              placeholder="Enter your feedback..."
              value={feedbackText[item.item_id] || ''}
              onChange={(e) => handleFeedbackChange(item.item_id, e.target.value)}
              style={{ marginBottom: '8px' }}
            />
            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                size="small"
                loading={isFeedbackSubmitting}
                onClick={() => handleFeedbackSubmit(item, sectionId)}
                style={{
                  backgroundColor: '#6200EE',
                  borderColor: '#6200EE'
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        )}

        {/* Display existing feedback from data */}
        {existingFeedback && !isFeedbackExpanded && (
          <div style={{
            marginTop: '4px',
            padding: '8px 12px',
            backgroundColor: '#f6f6f6',
            borderRadius: '4px',
            borderLeft: '3px solid #6200EE'
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>Feedback:</strong> {existingFeedback}
            </Text>
          </div>
        )}
      </div>
    );
  };

  // Render a section
  const renderSection = (section) => {
    // Don't render empty sections
    if (!section.items || section.items.length === 0) {
      return null;
    }

    // Section style mapping
    const sectionStyles = {
      default: {},
      muted: { opacity: 0.7 },
      warning: { borderLeft: '3px solid #faad14', paddingLeft: '12px' },
      success: { borderLeft: '3px solid #389e0d', paddingLeft: '12px' }
    };

    const style = sectionStyles[section.section_style] || sectionStyles.default;
    
    // Sections that will be accepted (show green checkmark)
    const acceptedSections = ['affirmed', 'user_affirmed', 'user_restored'];
    const isAcceptedSection = acceptedSections.includes(section.section_id);

    return (
      <div key={section.section_id} style={{ marginBottom: '24px', ...style }}>
        {/* Section header */}
        {section.section_label && (
          <div style={{ 
            marginBottom: '12px',
            borderBottom: '1px solid #333',
            paddingBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Text strong style={{ fontSize: '15px' }}>
              {section.section_label}
            </Text>
            {isAcceptedSection && (
              <CheckOutlined style={{ color: '#389e0d', fontSize: '14px' }} />
            )}
          </div>
        )}

        {/* Section items */}
        <div>
          {section.items.map((item) => renderItem(item, section.section_id))}
        </div>
      </div>
    );
  };

  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;

  // Get display title
  const displayTitle = data?.title || title;

  return (
    <div style={{ 
      marginBottom: '4px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          expandIconRef.current = isActive;
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {displayTitle}
              </div>
            ),
            children: (
              <div>
                {/* Tab selection (if tabbed view) */}
                {data?.view_mode?.type === 'tabbed' && data?.view_mode?.tabs?.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <Space>
                      <Text strong style={{ fontSize: '14px' }}>Show</Text>
                      <Radio.Group 
                        value={activeTab} 
                        onChange={handleTabChange}
                        optionType="button"
                        buttonStyle="outline"
                      >
                        {data.view_mode.tabs.map((tab) => (
                          <Radio.Button key={tab.id} value={tab.id} style={{ fontWeight: 500, fontSize: '14px' }}>
                            {tab.label}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Space>
                  </div>
                )}

                {/* Description */}
                {data?.description && (
                  <Text style={{ display: 'block', marginBottom: '16px' }}>
                    {data.description}
                  </Text>
                )}

                {/* Sections */}
                <div>
                  {getActiveSections().map((section) => renderSection(section))}
                </div>

                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      size="small" 
                      icon={<DownOutlined style={{ transform: 'rotate(90deg)' }} />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </Button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <Button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default ReviewPanel;